import { useEffect } from 'react';
import { addMonths, format } from 'date-fns';
import { toast } from 'react-toastify';
import { PERIODICIDADE_PARA_MESES } from '../constants.js';
import { isNumeroPositivoValido, isSimulaParcelaValida } from '../utils/recebivelValidators.js';

/**
 * @typedef {object} UseRecebivelSimulacaoParams
 * @property {string} periodicidade
 * @property {Date|string|null} recebivelTipoDataInicio
 * @property {(value: Date|string|null) => void} setRecebivelTipoDataFim
 * @property {(value: string) => void} setMostraDateSimulado
 * @property {number|string|null} simulaParcela
 * @property {number|string|null} recebivelTipoValor
 * @property {number|string|null} recebivelTipoDescontoSimula
 * @property {string} recebivelTipoDescontoTipoSimula
 * @property {(value: boolean) => void} setDescontoAtivo
 * @property {(value: number) => void} setParcelaSimulado
 * @property {(value: number|string) => void} setValorParcela
 * @property {number|string|null} valorParcela
 * @property {(value: boolean) => void} setShowSimula
 * @property {(value: number) => void} setRecebivelTipoValor
 */

/**
 * Hook com o motor da simulacao local de parcelas.
 *
 * @param {UseRecebivelSimulacaoParams} params
 */
export function useRecebivelSimulacao(params) {
    const {
        periodicidade,
        recebivelTipoDataInicio,
        setRecebivelTipoDataFim,
        setMostraDateSimulado,
        simulaParcela,
        recebivelTipoValor,
        recebivelTipoDescontoSimula,
        recebivelTipoDescontoTipoSimula,
        setDescontoAtivo,
        setParcelaSimulado,
        setValorParcela,
        valorParcela,
        setShowSimula,
        setRecebivelTipoValor,
    } = params;

    /**
     * Arredonda um valor monetario para 2 casas decimais.
     *
     * @param {number} valor
     * @returns {number}
     */
    function arredondaMoeda(valor) {
        return Math.round((Number(valor) + Number.EPSILON) * 100) / 100;
    }

    /**
     * Converte a periodicidade selecionada para a quantidade de meses.
     *
     * @param {string} frequencia
     * @returns {number}
     */
    function getMesesPeriodicidade(frequencia) {
        return PERIODICIDADE_PARA_MESES[frequencia] ?? 1;
    }

    /**
     * Calcula a data do ultimo vencimento considerando a primeira parcela como indice 0.
     *
     * @param {Date|string|null} primeiraData
     * @param {number|string|null} numeroParcelas
     * @param {number} stepMonths
     * @returns {Date|null}
     */
    function getUltimoVencimento(primeiraData, numeroParcelas, stepMonths) {
        const parcelasNumero = Number(numeroParcelas);
        const stepNumero = Number(stepMonths);
        const dataBase = new Date(primeiraData);

        if (!Number.isFinite(parcelasNumero) || parcelasNumero <= 0) {
            return null;
        }

        if (!Number.isFinite(stepNumero) || stepNumero <= 0) {
            return null;
        }

        if (Number.isNaN(dataBase.getTime())) {
            return null;
        }

        const deslocamentoMeses = (parcelasNumero - 1) * stepNumero;
        return addMonths(dataBase, deslocamentoMeses);
    }

    /**
     * Normaliza o valor digitado no campo de parcelas para inteiro valido.
     *
     * @param {string|number|null|undefined} rawValue
     * @returns {number|null}
     */
    function parseNumeroParcelasInput(rawValue) {
        if (rawValue === null || rawValue === undefined) {
            return null;
        }

        const sanitizedValue = String(rawValue).trim().replace(',', '.');
        const parsedValue = Number(sanitizedValue);

        if (!Number.isFinite(parsedValue) || parsedValue < 1) {
            return null;
        }

        return Math.trunc(parsedValue);
    }

    /**
     * Calcula e salva o valor de cada parcela no simulador local.
     *
     * @param {number} parcelas
     * @param {number} valorCada
     */
    function calculaParcela(parcelas, valorCada) {
        const parcelasNumero = Number(parcelas);
        const valorNumero = Number(valorCada);

        if (!Number.isFinite(parcelasNumero) || parcelasNumero <= 0 || !Number.isFinite(valorNumero)) {
            setDescontoAtivo(false);
            setValorParcela(0);
            return;
        }

        setDescontoAtivo(true);
        setValorParcela(arredondaMoeda(valorNumero / parcelasNumero));
    }

    /**
     * Calcula o resumo da simulacao local:
     * - data do ultimo vencimento
     * - valor total com/sem desconto
     * - valor individual por parcela
     */
    function calculaDesconto() {
        const parcelas = Number(simulaParcela);
        const valorTotal = Number(recebivelTipoValor);
        const descontoInformado = Number(recebivelTipoDescontoSimula);
        const parcelasValidas = isSimulaParcelaValida(parcelas);
        const valorTotalValido = isNumeroPositivoValido(valorTotal);

        if (recebivelTipoDataInicio && parcelasValidas) {
            const mesesPeriodicidade = getMesesPeriodicidade(periodicidade);
            const dataUltimoVencimento = getUltimoVencimento(recebivelTipoDataInicio, parcelas, mesesPeriodicidade);

            if (dataUltimoVencimento) {
                setRecebivelTipoDataFim(dataUltimoVencimento);
                setMostraDateSimulado(format(dataUltimoVencimento, 'dd-MM-yyyy'));
            } else {
                setRecebivelTipoDataFim(null);
                setMostraDateSimulado('');
            }
        } else {
            setRecebivelTipoDataFim(null);
            setMostraDateSimulado('');
        }

        if (!parcelasValidas || !valorTotalValido) {
            setDescontoAtivo(false);
            setParcelaSimulado(0);
            setValorParcela(0);
            return;
        }

        if (recebivelTipoDescontoTipoSimula === '%') {
            const descontoPercentual = Number.isFinite(descontoInformado) ? descontoInformado : 0;
            const valorDesconto = valorTotal * (descontoPercentual / 100);
            const valorDescontado = arredondaMoeda(valorTotal - valorDesconto);

            setParcelaSimulado(valorDescontado);
            calculaParcela(parcelas, valorDescontado);
            return;
        }

        if (recebivelTipoDescontoTipoSimula === 'R$') {
            const descontoValor = Number.isFinite(descontoInformado) ? descontoInformado : 0;
            const valorDescontado = arredondaMoeda(valorTotal - descontoValor);

            setParcelaSimulado(valorDescontado);
            calculaParcela(parcelas, valorDescontado);
            return;
        }

        setDescontoAtivo(false);
        setParcelaSimulado(0);
        setValorParcela(arredondaMoeda(valorTotal / parcelas));
    }

    /**
     * Confirma a simulacao e injeta o valor da parcela no formulario principal.
     */
    function handleConfirmSimula() {
        const valorParcelaNumero = Number(valorParcela);

        if (!Number.isFinite(valorParcelaNumero) || valorParcelaNumero <= 0) {
            toast.error('Informe um valor total e um número de parcelas válido para simular.');
            return;
        }

        if (valorParcelaNumero < 10) {
            toast.error('O menor valor de cada boleto deve ser maior que R$: 10.');
            return;
        }

        setShowSimula(false);
        setDescontoAtivo(false);
        setRecebivelTipoValor(arredondaMoeda(valorParcelaNumero));
    }

    useEffect(() => {
        calculaDesconto();
    }, [recebivelTipoValor, recebivelTipoDescontoTipoSimula, recebivelTipoDescontoSimula, recebivelTipoDataInicio, simulaParcela, periodicidade]);

    return {
        arredondaMoeda,
        getMesesPeriodicidade,
        getUltimoVencimento,
        parseNumeroParcelasInput,
        calculaDesconto,
        calculaParcela,
        handleConfirmSimula,
    };
}
